import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { api } from '../api/client.ts';
import { useMe } from '../api/hooks.ts';
import { HeaderBar } from '../components/HeaderBar.tsx';
import { Icon } from '../components/Icon.tsx';
import { Section } from '../components/Section.tsx';
import { DEFAULTS } from '@shared/constants.ts';
import { ToggleSwitch } from '../components/sheets/SheetShell.tsx';
import { ensurePushSubscription, removePushSubscription } from '../lib/push-client.ts';
import { DeviceLinkSheet } from '../components/sheets/DeviceLinkSheet.tsx';
import { QuickLogEditorSheet } from '../components/sheets/QuickLogEditorSheet.tsx';

type Theme = 'auto' | 'light' | 'dark';

async function downloadExport(babyId: string | null | undefined, format: 'json' | 'csv') {
  if (!babyId) {
    toast.error('No active baby');
    return;
  }
  try {
    const r = await fetch(`/api/export?baby=${encodeURIComponent(babyId)}&format=${format}`, {
      credentials: 'include',
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lion-tracker-${new Date().toISOString().slice(0, 10)}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5_000);
    toast.success(`Downloaded ${format.toUpperCase()}`);
  } catch (e) {
    toast.error(e instanceof Error ? e.message : 'Export failed');
  }
}

export function Settings() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [theme, setTheme] = useState<Theme>(
    (localStorage.getItem('lt_theme') as Theme) || 'auto',
  );
  const [deviceSheet, setDeviceSheet] = useState(false);
  const [gridEditor, setGridEditor] = useState(false);
  const [betaMonitor, setBetaMonitor] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('lt-beta-monitor') === '1',
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const t = theme === 'auto' ? (mq.matches ? 'dark' : 'light') : theme;
      document.documentElement.dataset.theme = t;
    };
    apply();
    localStorage.setItem('lt_theme', theme);
    if (theme === 'auto') mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);

  // We await refetchQueries (not just invalidate) so the new `me` snapshot
  // lands before the editor sheet closes — otherwise navigating back to /
  // shows the stale tile order until the next poll lap.
  const patchSettings = useMutation({
    mutationFn: (vars: Record<string, unknown>) =>
      api('/api/settings', { method: 'PATCH', json: vars }),
    onSuccess: () => qc.refetchQueries({ queryKey: ['me'] }),
  });

  const patchNotif = useMutation({
    mutationFn: (vars: Record<string, boolean>) =>
      api('/api/settings/notifications', { method: 'PATCH', json: vars }),
    onSuccess: () => qc.refetchQueries({ queryKey: ['me'] }),
  });

  async function logout() {
    try {
      await api('/api/auth/logout', { method: 'POST' });
      qc.clear();
      nav('/login', { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Logout failed');
    }
  }

  const s = me?.settings;
  const prefs = me?.notification_prefs;

  return (
    <>
      <HeaderBar title="Settings" showAvatar={false} />
      <div className="lt-scroll" style={{ flex: 1 }}>
        <Section title="Appearance">
          <Row leading={<TileIcon c="var(--accent)"><Icon.sparkle s={18} /></TileIcon>} title="Theme">
            <SegInline
              value={theme}
              onChange={(v) => setTheme(v)}
              options={[
                { id: 'auto', l: 'Auto' },
                { id: 'light', l: 'Light' },
                { id: 'dark', l: 'Dark' },
              ]}
            />
          </Row>
          <Row
            leading={<TileIcon c="var(--text-2)"><Icon.clock s={18} /></TileIcon>}
            title="Week starts on"
            last
          >
            <SegInline
              value={(s?.week_start ?? 'monday') as 'monday' | 'sunday'}
              onChange={(v) => patchSettings.mutate({ week_start: v })}
              options={[
                { id: 'monday', l: 'Mon' },
                { id: 'sunday', l: 'Sun' },
              ]}
            />
          </Row>
        </Section>

        <Section title="Button grid">
          <Row
            leading={<TileIcon c="var(--accent)"><Icon.edit s={18} /></TileIcon>}
            title="Edit quick log"
            subtitle="Reorder or hide tiles"
            onClick={() => setGridEditor(true)}
          />
          <Row
            leading={<TileIcon c="var(--text-2)"><Icon.chart s={18} /></TileIcon>}
            title="Density"
            last
          >
            <SegInline
              value={(s?.density ?? '3col') as '2col' | '3col'}
              onChange={(v) => patchSettings.mutate({ density: v })}
              options={[
                { id: '2col', l: '2 cols' },
                { id: '3col', l: '3 cols' },
              ]}
            />
          </Row>
        </Section>

        <Section title="Growth & curves" hint="WHO 2006 is the international healthy-breastfed reference; NL5 is the current Dutch standard (used by the consultatiebureau). Pick whichever your paediatrician uses.">
          <ToggleRow
            label="Show growth curves on chart"
            hint="Percentile bands behind the data"
            on={!!s?.growth_show_curves}
            onChange={(v) => patchSettings.mutate({ growth_show_curves: v })}
          />
          <Row
            leading={<TileIcon c="var(--text-2)"><Icon.chart s={18} /></TileIcon>}
            title="Reference"
          >
            <SegInline
              value={(s?.growth_reference ?? 'nl5') as 'nl3' | 'nl4' | 'nl5' | 'who'}
              onChange={(v) => patchSettings.mutate({ growth_reference: v })}
              options={[
                { id: 'nl5', l: 'NL5' },
                { id: 'nl4', l: 'NL4' },
                { id: 'nl3', l: 'NL3' },
                { id: 'who', l: 'WHO' },
              ]}
            />
          </Row>
          <ToggleRow
            label="Show target adult height"
            hint="From biological parents' heights"
            on={!!s?.growth_show_target_height}
            onChange={(v) => patchSettings.mutate({ growth_show_target_height: v })}
            last
          />
        </Section>

        <Section title="Feeding">
          <ToggleRow
            label="Merge L / R into one Breast tile"
            hint="Tap opens a Left / Right picker sheet"
            on={!!s?.merge_breast}
            onChange={(v) => patchSettings.mutate({ merge_breast: v })}
            last
          />
        </Section>

        <Section title="Nudges & auto-end" hint="Adjustable per-baby. Defaults to Lion's settings.">
          <NumberRow
            label="Sleep nudge after"
            unit="min"
            value={s?.sleep_max_minutes ?? DEFAULTS.sleepMaxMinutes}
            onChange={(v) => patchSettings.mutate({ sleep_max_minutes: v })}
            min={30}
            max={720}
            step={15}
            detail={`auto-ends after ${s?.sleep_auto_end_minutes ?? 360} min`}
          />
          <NumberRow
            label="Breast feed nudge after"
            unit="min"
            value={s?.feed_max_minutes ?? DEFAULTS.feedMaxMinutes}
            onChange={(v) => patchSettings.mutate({ feed_max_minutes: v })}
            min={5}
            max={300}
            step={5}
            detail="never auto-ends"
          />
          <NumberRow
            label="Pump nudge after"
            unit="min"
            value={s?.pump_max_minutes ?? DEFAULTS.pumpMaxMinutes}
            onChange={(v) => patchSettings.mutate({ pump_max_minutes: v })}
            min={5}
            max={300}
            step={5}
            detail={`needs-review flag after ${s?.pump_auto_end_minutes ?? 120} min`}
            last
          />
        </Section>

        <Section title="Notifications">
          <ToggleRow
            label="Forgot-to-stop nudges"
            hint="Push when a session has run too long"
            on={!!prefs?.forgot_to_stop}
            onChange={async (v) => {
              patchNotif.mutate({ forgot_to_stop: v });
              try {
                if (v) {
                  const ok = await ensurePushSubscription();
                  if (!ok) toast.error('Push not available — install to home screen on iOS');
                } else {
                  await removePushSubscription();
                }
              } catch (e) {
                toast.error(e instanceof Error ? e.message : 'Push failed');
              }
            }}
          />
          <ToggleRow
            label="Partner activity"
            hint="Toast when someone else logs"
            on={!!prefs?.partner_activity}
            onChange={(v) => patchNotif.mutate({ partner_activity: v })}
          />
          <ToggleRow
            label="Feed interval reminders"
            hint="Coming soon"
            on={!!prefs?.feed_reminders}
            onChange={(v) => patchNotif.mutate({ feed_reminders: v })}
            last
          />
        </Section>

        <Section
          title="Beta"
          hint="Experimental features. Things may break. Toggling reveals new menu items elsewhere in the app."
        >
          <ToggleRow
            label="Baby Monitor"
            hint="Stream audio from a phone near baby to other family devices. Requires the broadcasting phone to stay unlocked."
            on={betaMonitor}
            onChange={(v) => {
              setBetaMonitor(v);
              localStorage.setItem('lt-beta-monitor', v ? '1' : '0');
              if (v) toast.success('Baby Monitor enabled — see "Monitor" under More.');
            }}
            last
          />
        </Section>

        <Section title="This device">
          <Row
            leading={<TileIcon c="var(--accent)"><Icon.share s={18} /></TileIcon>}
            title="Add another device"
            subtitle="Scan a QR code or open the link on your other phone"
            onClick={() => setDeviceSheet(true)}
            last
          />
        </Section>

        <Section title="Data">
          <Row
            leading={<TileIcon c="var(--text-2)"><Icon.share s={18} /></TileIcon>}
            title="Export Lion's data (JSON)"
            subtitle="Download every event"
            onClick={() => downloadExport(me?.active_baby_id, 'json')}
          />
          <Row
            leading={<TileIcon c="var(--text-2)"><Icon.share s={18} /></TileIcon>}
            title="Export Lion's data (CSV)"
            subtitle="Spreadsheet-friendly"
            onClick={() => downloadExport(me?.active_baby_id, 'csv')}
          />
          <Row
            leading={<TileIcon c="var(--danger)"><Icon.trash s={18} /></TileIcon>}
            title="Sign out"
            subtitle={me?.user.email ?? me?.user.name}
            onClick={logout}
            last
          />
        </Section>

        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-3)', padding: '0 0 30px' }}>
          Lion Tracker · v0.1.0
        </div>
      </div>

      <DeviceLinkSheet open={deviceSheet} onClose={() => setDeviceSheet(false)} />
      <QuickLogEditorSheet
        open={gridEditor}
        onClose={() => setGridEditor(false)}
        value={s?.button_grid_config?.tiles ?? null}
        mergeBreast={!!s?.merge_breast}
        onSave={(tiles) =>
          patchSettings.mutateAsync({
            button_grid_config: { tiles, density: (s?.density ?? '3col') as '2col' | '3col' },
          })
        }
      />
    </>
  );
}

function TileIcon({ c, children }: { c: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 11,
        background: `color-mix(in oklab, ${c} 16%, transparent)`,
        color: c,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </div>
  );
}

function Row({
  leading,
  title,
  subtitle,
  children,
  onClick,
  last,
}: {
  leading: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  onClick?: () => void;
  last?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderBottom: last ? 'none' : '1px solid var(--divider)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {leading}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 14 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function SegInline<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; l: string }[];
}) {
  return (
    <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--bg-sunken)', borderRadius: 999 }}>
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            type="button"
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              padding: '5px 12px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: on ? 'var(--bg-elevated)' : 'transparent',
              color: on ? 'var(--text-1)' : 'var(--text-3)',
              boxShadow: on ? 'var(--shadow-sm)' : 'none',
            }}
          >
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

function NumberRow({
  label,
  unit,
  value,
  onChange,
  min,
  max,
  step,
  detail,
  last,
}: {
  label: string;
  unit: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  detail?: string;
  last?: boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
          {detail && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{detail}</div>}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            padding: 3,
            background: 'var(--bg-sunken)',
            borderRadius: 999,
          }}
        >
          <button
            type="button"
            onClick={() => onChange(Math.max(min, value - step))}
            style={{ width: 28, height: 24, borderRadius: 999, fontWeight: 600 }}
          >
            −
          </button>
          <span className="lt-tabular" style={{ padding: '0 8px', fontSize: 13, fontWeight: 600, minWidth: 50, textAlign: 'center' }}>
            {value} {unit}
          </span>
          <button
            type="button"
            onClick={() => onChange(Math.min(max, value + step))}
            style={{ width: 28, height: 24, borderRadius: 999, fontWeight: 600 }}
          >
            +
          </button>
        </div>
      </div>
      {!last && <div style={{ height: 1, background: 'var(--divider)', marginLeft: 16 }} />}
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  on,
  onChange,
  last,
}: {
  label: string;
  hint?: string;
  on: boolean;
  onChange: (v: boolean) => void;
  last?: boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
          {hint && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{hint}</div>}
        </div>
        <ToggleSwitch on={on} onChange={onChange} />
      </div>
      {!last && <div style={{ height: 1, background: 'var(--divider)', marginLeft: 16 }} />}
    </div>
  );
}
